import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import { supabase } from '../../supabase.config';
import BuildingCard from '../../components/availability/BuildingCard';
import { calculateAvailability, parseTimeString } from '../../components/availability/utils/timeUtils';
import AvailabilityHeader from '../../components/availability/AvailabilityHeader';
import { Search } from 'lucide-react';

const ClassroomFinder = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [buildings, setBuildings] = useState({});
  const [courseData, setCourseData] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showTimeFilter, setShowTimeFilter] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState(() => {
    const saved = localStorage.getItem('selectedTimeRange');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch lecture halls with building info from Supabase
        const { data: lectureHalls, error } = await supabase
          .from('LectureHall')
          .select(`
            *,
            building:Building(*),
            photos:Photo(*)
          `);

        if (error) throw error;

        // Group by building name
        const buildingMap = {};
        lectureHalls.forEach((hall) => {
          const buildingName = hall.building?.name || 'Unknown';
          
          if (!buildingMap[buildingName]) {
            buildingMap[buildingName] = {};
          }

          buildingMap[buildingName][hall.room] = {
            id: hall.id,
            building: buildingName,
            room: hall.room,
            photos: hall.photos?.map(p => p.url) || [],
            hasAvInputs: hall.hasAvInputs,
            hasPc: hall.hasPc,
            hasWhiteboard: hall.hasWhiteboard,
            hasProjector: hall.hasProjector,
            // Legacy format for compatibility
            information: {
              av_inputs: hall.hasAvInputs ? 'yes' : 'no',
              pc: hall.hasPc ? 'yes' : 'no',
              whiteboard: hall.hasWhiteboard ? 'yes' : 'no',
              projector: hall.hasProjector ? 'yes' : 'no'
            }
          };
        });

        // Fetch and parse course schedule CSV
        const response = await fetch('/data/cleaned_courses_f26.csv');
        const csvText = await response.text();

        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const coursesByRoom = {};

            results.data.forEach((course) => {
              if (!course.Building || !course.Room) return;

              let keys = [];
              if (course.Building === 'MLU') {
                const roomNumber = course.Room.startsWith('MLU') ? 
                  course.Room.substring(3) : course.Room;
                keys.push(`MLU-${roomNumber}`);
                keys.push(`${course.Building}-${roomNumber}`);
              } else {
                keys.push(`${course.Building}-${course.Room}`);
              }

              const days = course.Days.split(',').map((day) => day.trim());
              const courseData = { ...course, Days: days };

              keys.forEach(key => {
                if (!coursesByRoom[key]) coursesByRoom[key] = [];
                const exists = coursesByRoom[key].some(
                  existingCourse => 
                    existingCourse['Time Range'] === course['Time Range'] &&
                    existingCourse.Days.join(',') === days.join(',')
                );
                if (!exists) coursesByRoom[key].push(courseData);
              });
            });

            Object.keys(coursesByRoom).forEach(key => {
              coursesByRoom[key].sort((a, b) => {
                const aTime = a['Time Range'].split(' - ')[0];
                const bTime = b['Time Range'].split(' - ')[0];
                return parseTimeString(aTime) - parseTimeString(bTime);
              });
            });

            setCourseData(coursesByRoom);
            setBuildings(buildingMap);
            setLoading(false);
          },
          error: (error) => setLoading(false)
        });

      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleTimeChange = (timeRange) => {
    setSelectedTimeRange(timeRange);
    setActiveFilter('time');
    localStorage.setItem('selectedTimeRange', JSON.stringify(timeRange));
  };

  const handleFilterClick = (filter) => {
    if (filter === activeFilter) {
      setActiveFilter('all');
      setShowTimeFilter(false);
      setSelectedTimeRange(null);
      localStorage.removeItem('selectedTimeRange');
    } else {
      setActiveFilter(filter);
      setShowTimeFilter(filter === 'time');
    }
  };

  const filteredBuildings = Object.entries(buildings)
    .filter(([building]) => building.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter(([building, rooms]) => {
      if (activeFilter === 'all') return true;
      if (activeFilter === 'available' || activeFilter === 'time') {
        const { available } = calculateAvailability(building, rooms, courseData, selectedTimeRange);
        return available > 0;
      }
      return true;
    });

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-24 pb-12">
      <AvailabilityHeader 
        activeFilter={activeFilter}
        handleFilterClick={handleFilterClick}
        showTimeFilter={showTimeFilter}
        handleTimeChange={handleTimeChange}
        setShowTimeFilter={setShowTimeFilter}
        selectedTimeRange={selectedTimeRange}
        setSelectedTimeRange={setSelectedTimeRange}
        setActiveFilter={setActiveFilter}
      />

      <div className="max-w-7xl mx-auto px-4">
        {/* Search Bar */}
        <div className="mb-8 relative max-w-md mx-auto md:mx-0">
          <input
            type="text"
            placeholder="Search buildings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-video bg-white/5 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
            {filteredBuildings.map(([building, rooms]) => {
              const { available, totalClasses } = calculateAvailability(building, rooms, courseData, selectedTimeRange);
              return (
                <BuildingCard
                  key={building}
                  building={building}
                  rooms={Object.values(rooms)}
                  available={available}
                  totalClasses={totalClasses}
                />
              );
            })}
            
            {filteredBuildings.length === 0 && (
              <div className="col-span-full text-center py-20 text-gray-500">
                No buildings found matching your criteria
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassroomFinder;
